import {
  contrastRatio,
  contrastColor,
  resolvedDefault,
  CONTRAST_MIN,
} from './themeTokens';

/**
 * WCAG contrast audit for a whole theme.
 *
 * WHY THIS EXISTS
 * Two bespoke detectors shipped in round 2 -- `modalSuggestion` and
 * `tabSuggestion` -- because the dialog card and the filled tab pill are the
 * only two grounds the editor could compute without guessing. Everything else
 * went unchecked, so a client could set Soft Text to `#ffffff` and the Builder
 * would say nothing while the player rendered it invisible.
 *
 * THE GROUND PROBLEM, AND WHAT WE DO ABOUT IT
 * The player paints no background on purpose (`global.css:60`): it imposes
 * nothing, so a consumer never has to override one. `--player-surface` is an
 * opt-in panel background that is not a PMM key, so nothing here can set it,
 * and it defaults to transparent. The practical consequence is that most inks
 * in this system sit on the CLIENT'S PAGE, which Control Center does not know.
 *
 * So the audit takes the ground as an argument. The Builder passes whatever
 * the preview is currently set to -- the light/dark toggle the user can see
 * and flip -- and every finding names the assumption it made. A warning that
 * says "against a light page" is honest in a way that an unqualified "fails
 * contrast" is not, and it flips the moment the user says their site is dark.
 *
 * NOT A GATE
 * Advisory, like the two detectors before it. Findings are returned; nothing
 * is applied and nothing is blocked. The un-themed defaults are legible on a
 * light page, so this guides rather than obstructs.
 */

/** WCAG 1.4.11: non-text UI components and graphical objects. */
export const NON_TEXT_MIN = 3;

/** The two page grounds the preview can be set to. */
export const PAGE_LIGHT = '#ffffff';
export const PAGE_DARK = '#1b1b1b';

/**
 * A ground of `PAGE` means "whatever is behind the player" -- resolved from
 * the caller's assumption rather than from a token.
 */
const PAGE = 'page';

/**
 * What sits on what.
 *
 * `grounds` is a preference order, not a set: the first candidate that
 * actually paints something wins, and `PAGE` terminates every chain because
 * a transparent ground shows the page through it. That ordering is why six of
 * these rules still end up measured against the page -- their grounds default
 * to transparent, so unless the client sets one, the page IS the ground.
 *
 * `min` is the WCAG threshold: 4.5 for text (AA), 3 for non-text (1.4.11).
 *
 * Deliberately NOT modelled: large-text's 3:1 allowance. `themeTitleFontSize`
 * is settable, so a big title legitimately qualifies -- but `themeTextStrong`
 * also paints the active tab label at ~12px, and the binding constraint is the
 * smallest use. Holding it to 4.5 is correct for the token even where it is
 * conservative for one of its surfaces.
 */
export const CONTRAST_RULES = [
  // --- inks that have no ground but the page ---
  { ink: 'themeTextStrong', grounds: [PAGE], min: CONTRAST_MIN, where: 'The event title and active tab label' },
  { ink: 'themeText', grounds: [PAGE], min: CONTRAST_MIN, where: 'Body copy and the event description' },
  { ink: 'themeTextMuted', grounds: [PAGE], min: CONTRAST_MIN, where: 'The date, location, button labels and transcript body' },
  // 13 usages, all text, several at 11px where no large-text allowance
  // applies -- including a `role="status" aria-live="polite"` message
  // (TranscriptList.tsx:271) that a screen reader announces and a sighted
  // user is expected to read at 2.52:1.
  { ink: 'themeTextSoft', grounds: [PAGE], min: CONTRAST_MIN, where: 'Transcript and agenda timecodes, placeholders and status messages' },
  // Accent paints text in exactly three places, all of them inside dialogs
  // and menus: the selected download option (DownloadDialog.tsx:154) and two
  // labels in the settings menu (SettingsMenu.tsx:179, :186). So the ground is
  // the DIALOG CARD, not the page -- which also means a client with a dark
  // themeModal gets measured against their card rather than against a page
  // their dialogs do not sit on.
  //
  // Its other uses are not text and are not audited here: the active tab
  // indicator is a border, the settings checkmark is a state glyph, and the
  // share checkbox rides the native `accent-color`. All three fall under
  // 1.4.11's 3:1, which the shipped default clears at 3.68.
  { ink: 'themeAccent', grounds: ['themeModal', PAGE], min: CONTRAST_MIN, where: 'Labels in the download and settings menus' },
  { ink: 'themeUnderlayDateColor', grounds: [PAGE], min: CONTRAST_MIN, where: 'The event date line' },

  // --- non-text ---
  // ONLY the focus ring. 1.4.11 covers visual information REQUIRED TO IDENTIFY
  // a component or its state -- not decoration -- and a focus indicator a
  // keyboard user cannot see is an unambiguous 2.4.11 failure.
  //
  // Borders are deliberately excluded, after measuring. `themeBorder` is a
  // hairline divider whose shipped default is 1.48:1 on white, and the toggle
  // outlines run 1.43 and 1.87 beside fills that already carry the state.
  // Auditing them at 3:1 flagged every theme including the one literally named
  // Accessible, which is the signature of a rule applied where it does not
  // belong rather than of six broken themes. A divider carries no state; the
  // fill beside it does.
  { ink: 'themeFocusRing', grounds: [PAGE], min: NON_TEXT_MIN, where: 'The keyboard focus ring', nonText: true },

  // --- inks with a ground of their own, which may still be transparent ---
  { ink: 'themeButtonText', grounds: ['themeButtonBg', PAGE], min: CONTRAST_MIN, where: 'The share and download button labels' },
  { ink: 'themeTabColor', grounds: ['themeTabBg', 'themeTabStripBg', PAGE], min: CONTRAST_MIN, where: 'The unselected tab labels' },
  { ink: 'themeTabActiveText', grounds: ['themeTabActiveBg', 'themeTabStripBg', PAGE], min: CONTRAST_MIN, where: 'The selected tab label' },
  { ink: 'themeListRowColor', grounds: ['themeListRowBg', PAGE], min: CONTRAST_MIN, where: 'Document and agenda row labels' },
  { ink: 'themeListRowColor', grounds: ['themeListRowBgAlt', PAGE], min: CONTRAST_MIN, where: 'Document and agenda alternate rows' },
  { ink: 'themeTranscriptRowColor', grounds: ['themeTranscriptRowBg', 'themeListRowBg', PAGE], min: CONTRAST_MIN, where: 'Transcript lines' },
  { ink: 'themeHighlightToggleColor', grounds: ['themeHighlightToggleBg', PAGE], min: CONTRAST_MIN, where: 'The Highlight button label when it is on' },
  { ink: 'themeFollowToggleColor', grounds: ['themeFollowToggleBg', PAGE], min: CONTRAST_MIN, where: 'The Follow button label when it is on' },
];

/**
 * The smallest correction that clears the threshold, keeping the hue.
 *
 * `contrastColor` answers a different question -- "what ink reads on this
 * background" -- and answers it with maximum contrast. That is right for a
 * label on a filled pill and wrong as a FIX for a token that has a job in a
 * ramp: offered against Soft Text at #a3a3a3 it proposed #262626, which is as
 * dark as Strong Text. The suggestion cleared 4.5:1 by collapsing the
 * distinction the token exists to draw.
 *
 * So walk the colour toward the ground's opposite in small steps and stop at
 * the first value that passes. Soft Text becomes #767676 rather than #262626:
 * still the lightest ink in the theme, and now readable.
 *
 * Returns null for a colour we cannot parse, rather than inventing one.
 */
export function nudgeToPass (ink, ground, min) {
  const from = parseHexLocal(ink);
  const to = parseHexLocal(ground);
  if (!from || !to) return null;

  // Toward black on a light ground, toward white on a dark one.
  const target = relLum(to) > 0.5 ? [0, 0, 0] : [255, 255, 255];

  for (let step = 1; step <= 50; step++) {
    const amount = step / 50;
    const candidate = hexOf([
      from[0] + (target[0] - from[0]) * amount,
      from[1] + (target[1] - from[1]) * amount,
      from[2] + (target[2] - from[2]) * amount,
    ]);
    if (contrastRatio(candidate, ground) >= min) return candidate;
  }
  return null;
}

function parseHexLocal (value) {
  if (typeof value !== 'string') return null;
  let hex = value.trim().replace(/^#/, '');
  if (hex.length === 3) hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
  if (!/^[0-9a-fA-F]{6}$/.test(hex)) return null;
  return [
    parseInt(hex.slice(0, 2), 16),
    parseInt(hex.slice(2, 4), 16),
    parseInt(hex.slice(4, 6), 16),
  ];
}

function hexOf (rgb) {
  return '#' + rgb.map(c => {
    const v = Math.max(0, Math.min(255, Math.round(c)));
    return (v < 16 ? '0' : '') + v.toString(16);
  }).join('');
}

function relLum (rgb) {
  const channel = c => {
    const v = c / 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * channel(rgb[0]) + 0.7152 * channel(rgb[1]) + 0.0722 * channel(rgb[2]);
}

/** Does this value paint anything, or does whatever is behind it show through? */
function paints (value) {
  if (!value) return false;
  const v = String(value).trim().toLowerCase();
  if (v === 'transparent') return false;
  return !/^rgba\(\s*[\d.]+\s*,\s*[\d.]+\s*,\s*[\d.]+\s*,\s*0?\.?0*\s*\)$/.test(v);
}

/**
 * The value a token EFFECTIVELY paints: what the user set, or what the player
 * falls back to when they set nothing.
 *
 * Auditing the effective value rather than only the set one is the whole
 * point. A blank field is not neutral -- it paints a real colour, and on a
 * dark page the shipped defaults are exactly as unreadable as a bad choice
 * would be. That is not noise; it is the reason theming exists.
 */
function effective (key, properties) {
  return properties[key] || resolvedDefault(key, properties);
}

/**
 * Resolve a rule's ground to a colour, and say which it was.
 * Returns null when no candidate paints and there is no page to fall back to.
 */
function resolveGround (rule, properties, page) {
  for (let i = 0; i < rule.grounds.length; i++) {
    const candidate = rule.grounds[i];
    if (candidate === PAGE) {
      return { color: page, key: PAGE };
    }
    const value = effective(candidate, properties);
    if (paints(value)) return { color: value, key: candidate };
  }
  return null;
}

/**
 * Audit a theme.
 *
 * @param {object} properties        the preference being edited
 * @param {string} [page]            what is behind the player; defaults to light
 * @returns {Array<object>} findings, worst first
 */
export function auditContrast (properties, page) {
  if (!properties) return [];
  const ground = page || PAGE_LIGHT;
  const findings = [];

  CONTRAST_RULES.forEach(rule => {
    const ink = effective(rule.ink, properties);
    if (!paints(ink)) return;

    const on = resolveGround(rule, properties, ground);
    if (!on || !paints(on.color)) return;

    const ratio = contrastRatio(ink, on.color);
    // Null means one of the two is not a colour we can parse -- an `oklch()`
    // or `color-mix()` recipe. Silence beats a made-up number.
    if (ratio === null || ratio === undefined) return;
    if (ratio >= rule.min) return;

    // The nearest passing value, not the highest-contrast one -- a fix that
    // flattens the ramp is not a fix. Falls back to the maximum-contrast ink
    // only when the colour cannot be parsed and stepped.
    const suggestion = nudgeToPass(ink, on.color, rule.min) || contrastColor(on.color);
    findings.push({
      key: rule.ink,
      where: rule.where,
      // The failing colour itself, so the warning can render a live sample
      // rather than describe one.
      ink,
      ratio: Math.round(ratio * 100) / 100,
      required: rule.min,
      nonText: Boolean(rule.nonText),
      ground: on.color,
      // Names the assumption so a warning can say what it measured against
      // rather than asserting a fact it does not have.
      againstPage: on.key === PAGE,
      // Whether the failing value is one they chose or one they inherited --
      // the message differs, and so does the fix.
      inherited: !properties[rule.ink],
      fix: suggestion && suggestion !== ink ? suggestion : null,
    });
  });

  // Worst first: the ratio is the severity, and a rail showing several wants
  // the invisible one at the top.
  return findings.sort((a, b) => a.ratio - b.ratio);
}

/**
 * Findings keyed by the token they belong to, so a control can ask "is it me?"
 * without scanning the list on every render.
 */
export function findingsByKey (findings) {
  return (findings || []).reduce((acc, finding) => {
    (acc[finding.key] = acc[finding.key] || []).push(finding);
    return acc;
  }, {});
}

/**
 * Tint is not a contrast problem, it is a DIRECTION problem.
 *
 * It is composited at 5-12% opacity for hover and fill states, so a ratio
 * against it is meaningless -- at 8% almost nothing clears 4.5:1 and it is not
 * supposed to. What actually breaks is picking the tint that matches the page:
 * white tint on a white page means every hover state in the player does
 * nothing at all. The control's own description already says black on light,
 * white on dark; this is that sentence, enforced.
 */
export function tintDirectionWarning (properties, page) {
  if (!properties) return null;
  const tint = effective('themeTint', properties);
  if (!paints(tint)) return null;

  const ground = page || PAGE_LIGHT;
  const ratio = contrastRatio(tint, ground);
  if (ratio === null || ratio === undefined) return null;
  // Same threshold as a non-text component: below this the tint is too close
  // to the page for a hover state built from it to register at all.
  if (ratio >= NON_TEXT_MIN) return null;

  const wantsLight = contrastColor(ground) !== '#ffffff';
  // Same shape as a contrast finding, so the rail renders it through the same
  // path instead of growing a second warning component.
  return {
    key: 'themeTint',
    where: 'Hover and fill states are built from this color',
    ink: tint,
    tint,
    ground,
    ratio: Math.round(ratio * 100) / 100,
    required: NON_TEXT_MIN,
    nonText: true,
    inherited: !properties.themeTint,
    againstPage: true,
    fix: wantsLight ? '#000000' : '#ffffff',
    message: 'Hover and fill states are built from this color at low opacity. ' +
      'It is too close to the page behind the player, so those states will be invisible.',
  };
}

/**
 * What a failure MEANS, in words a clerk can act on.
 *
 * A ratio is the wrong unit for this audience. "3.68:1" carries no intuition
 * -- it is not a percentage, the scale is unfamiliar, and the eye slides off
 * it even when the sentence beside it explains the threshold. Someone
 * choosing a colour for a public meeting page needs to know whether people
 * can read it, not what it measures.
 *
 * The number is kept, demoted, because it is not useless to everyone: this
 * product is bought by public bodies who file accessibility conformance
 * reports, and their auditors want the figure.
 */
export function severityLabel (finding) {
  if (!finding) return '';
  if (finding.nonText) {
    return finding.ratio < 2 ? 'Almost invisible' : 'Too faint to make out';
  }
  if (finding.ratio < 2) return 'Almost invisible';
  if (finding.ratio < 3) return 'Very hard to read';
  return 'Hard to read for many viewers';
}

/**
 * Who it actually costs. Concrete and true rather than a statistic: low
 * contrast fails in bright light and for reduced vision, which describes a
 * public-meeting audience on a phone outdoors better than any figure would.
 */
export function severityConsequence (finding) {
  if (!finding) return '';
  return finding.nonText
    ? 'On a phone in daylight, or for a viewer with low vision, this may not be visible at all.'
    : 'On a phone in daylight, or for a viewer with low vision, this text can disappear.';
}
